import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography } from '@material-ui/core';
import { ErrorOutline, Refresh } from '@material-ui/icons';

import { PINPEO } from 'theme/palette';

/**
 * Red de seguridad para excepciones de render.
 *
 * Sin un boundary, cualquier throw durante render/commit desmonta el árbol
 * ENTERO y deja la pantalla en blanco, sin pista de lo que pasó. Con esto el
 * fallo queda acotado a la vista y el usuario conserva la navegación.
 *
 * Ojo: un boundary NO captura errores en handlers de eventos, en código
 * asíncrono ni en el propio boundary.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // console.error está permitido por la regla no-console del proyecto
    console.error('[ErrorBoundary]', error, info?.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    const { children } = this.props;

    if (!error) return children;

    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        style={{ height: '100%', gap: 12, padding: 24, textAlign: 'center' }}
      >
        <ErrorOutline style={{ fontSize: 40, color: PINPEO.textSecondary }} />
        <Typography style={{ fontSize: 16, fontWeight: 600, color: PINPEO.text }}>
          Algo salió mal en esta vista
        </Typography>
        <Typography style={{ fontSize: 13, color: PINPEO.textSecondary, maxWidth: 420 }}>
          El resto del panel sigue funcionando. Puedes recargar para volver a
          intentarlo o navegar a otra sección desde el menú lateral.
        </Typography>

        {process.env.NODE_ENV !== 'production' && (
          <Box
            component="pre"
            style={{
              marginTop: 8,
              padding: 12,
              maxWidth: '100%',
              overflow: 'auto',
              textAlign: 'left',
              fontSize: 11,
              borderRadius: 8,
              background: PINPEO.surface,
              border: `1px solid ${PINPEO.border}`,
              color: PINPEO.textSecondary,
            }}
          >
            {String(error?.stack || error?.message || error)}
          </Box>
        )}

        <Button
          variant="outlined"
          size="small"
          startIcon={<Refresh style={{ fontSize: 16 }} />}
          onClick={this.handleReload}
          style={{ marginTop: 4, textTransform: 'none', fontSize: 13, minHeight: 40 }}
        >
          Recargar
        </Button>
      </Box>
    );
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node,
};

export default ErrorBoundary;
